"use client";
import {
  BellOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Badge,
  Breadcrumb,
  Dropdown,
  Layout,
  Menu,
  Row,
  Tooltip,
} from "antd";
import { Header } from "antd/es/layout/layout";
import Sider from "antd/es/layout/Sider";
import { Outlet, useLocation, useNavigate } from "react-router";
import logo from "../assets/logo.svg";
import logo_two from "../assets/logo_two.svg";

import { getLocalStorageItem } from "../utils/Validation";
import BreadcrumbIcon from "../assets/BreadcrumbIcon.svg";
import React, { useEffect, useState } from "react";
import { usePermissions } from "../context/PermissionContext";
import CompanySearchDropdown from "../components/CompanySearchDropdown";
import apiService from "../services/apiService";
import type { MenuProps } from 'antd';
import { useCustomerStore } from '../stores/customerStore';
import { BookUser, Calculator, FileSpreadsheet, Handshake, UserKey, Landmark, ShieldHalf, UsersRound, Warehouse, ReceiptIndianRupee, ShoppingCart,CalendarDays, FileText, ReceiptText, StickyNote, Briefcase, LayoutDashboard } from "lucide-react";
import { useSidebar } from "../context/SidebarContext";
import useActiveStatusCheck from "../hooks/useActiveStatusCheck";

const DashboardLayout = () => {
  const { permissions } = usePermissions();
  const {
    canManageCustomers,
    canManageLeads,
    canManageRoles,
    canManagePartners,
    canManageLenders,
    canManageTermSheet,
    canManageCalculator,
    canManageLogin,
    canManageSupplier,
    canManagePurchaseOrder,
    canManageRentalSchedule,
    canManageRevenue,
    canManageRentalInvoice,
    canManagePostDisbursement,
    canManagePortfolio,
    canManageAdminDashboard,
  } = permissions;

  const location = useLocation();
  const navigate = useNavigate();
  const details = getLocalStorageItem("detail");

  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companies, setCompanies] = useState<{ id: string; name: string; cin: string }[]>([]);
  const { sidebarWidth, isSidebarHovered, setIsSidebarHovered } = useSidebar();
  const customer = useCustomerStore((s) => s.customer);

  const { status, loading } = useActiveStatusCheck();
  useEffect(() => {
    if (status && status.isActive === false) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/login";
    }
  }, [status, loading]);

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === "/") return "1";
    if (path === "/manage-role") return "2";
    if (path === "/manage-customer" || path.startsWith("/manage-customer/"))
      return "3";
    if (path === "/manage-partner") return "4";
    if (path === "/manage-lender") return "5";
    if (path === "/term-sheet") return "6";
    if (path === "/rental-calculator") return "7";
    if (path === "/manage-login") return "8";
    if (path === "/manage-supplier") return "9";
    if (path === "/manage-purchase-order") return "10";
    if (path === "/rental-schedule-dashboard") return "11";
    if (path === "/manage-revenue") return "12";
    if (path === "/manage-rental-invoice" || path === "/invoice-payment") return "13";

    if (path === "/manage-post-disbursement" || path.startsWith("/manage-post-disbursement/"))
      return "14";
    if (path === "/ViewPostDisbursement/:rsId" || path.startsWith("/ViewPostDisbursement/")) return "14";
    if (path === "/manage-portfolio" || path.startsWith("/manage-portfolio/")) return "15";
    if (path === "/admin-dashboard") return "16";

    return "1";
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("detail");
    navigate("/login");
  };

  const pathSnippets = location.pathname.split("/").filter((i) => i);
  const [firstSegment, secondSegment] = pathSnippets;


  useEffect(() => {
    setCompanyName(customer?.name || null);
  }, [customer]);


  useEffect(() => {
    // Fetch company list for dropdown
    apiService.get(`/customers/companyList`).then((response: any) => {
      if (Array.isArray(response.data)) {
        setCompanies(response.data as { id: string; name: string; cin: string }[]);
      } else {
        setCompanies([]);
      }
    });
  }, []);

  const breadcrumbItems = [
    {
      title: <a href="/">Home</a>,
    },
    ...pathSnippets
      .filter((_snippet, idx) => {
        // Remove company id from breadcrumb if on manage-customer route
        if (pathSnippets[0] === "manage-customer" && idx === 1) return false;
        return true;
      })
      .map((snippet, index) => {
        const url = `/${pathSnippets.slice(0, index + 1).join("/")}`;
        const isLast = index === pathSnippets.length - 1;
        const isCustomerDetail = pathSnippets[0] === "manage-customer" && pathSnippets[1];
        return {
          title: (
            <a href={url}>
              {isLast && isCustomerDetail
                ? null
                : snippet.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}
            </a>
          ),
        };
      }),
  ];

  const profileMenu = {
    items: [
      {
        key: "logout",
        label: "Logout",
        onClick: handleLogout,
      },
    ],
  };

  function getItem(
    label: string,
    key: string,
    icon: React.ReactNode,
    onClick: () => void
  ) {
    const iconWithTooltip = !isSidebarHovered ? (
      <Tooltip
        title={label}
        placement="right"
        color="white"
        styles={{ body: { color: "black" } }}
      >
        <div style={{ display: 'flex', justifyContent: 'center' }}>{icon}</div>
      </Tooltip>
    ) : icon;
    
    return {
      key,
      icon: iconWithTooltip,
      label: isSidebarHovered ? label : "",
      onClick,
    };
  }

  const items = [
    canManageAdminDashboard &&
    getItem(
      "Admin Dashboard",
      "16",
      <LayoutDashboard strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/admin-dashboard")
    ),
    canManageLeads &&
    getItem(
      "Manage Leads",
      "1",
      <BookUser size={24} strokeWidth={1.5} className="font-sm" style={{ fontSize: "20px" }} />,
      () => navigate("/lead")
    ),
    canManageRoles &&
    getItem(
      "Manage Team",
      "2",
      <ShieldHalf strokeWidth={1.5} className="font-sm" style={{ fontSize: "20px" }} />,
      () => navigate("/manage-role")
    ),
    canManageCustomers &&
    getItem(
      "Manage Customer",
      "3",
      <UsersRound strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/manage-customer")
    ),
    canManagePartners &&
    getItem(
      "Manage Partner",
      "4",
      <Handshake strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/manage-partner")
    ),
    canManageLenders &&
    getItem(
      "Manage Lender",
      "5",
      <Landmark strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/manage-lender")
    ),
    // canManageTermSheet &&
    // getItem(
    //   "Term Sheet",
    //   "6",
    //   <FileSpreadsheet strokeWidth={1.5} style={{ fontSize: "20px" }} />,
    //   () => navigate("/term-sheet")
    // ),
    canManageCalculator &&
    getItem(
      "Rental Calculation",
      "7",
      <Calculator strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/rental-calculator")
    ),
    canManageLogin &&
    getItem(
      "Manage Login",
      "8",
      <UserKey strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/manage-login")
    ),
    canManageSupplier &&
    getItem(
      "Manage Supplier",
      "9",
      <Warehouse strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/manage-supplier")
    ),
    canManagePurchaseOrder &&
    getItem(
      "Manage PO",
      "10",
      <ShoppingCart strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/manage-purchase-order")
    ),
    canManageRentalSchedule &&
    getItem(
      "Manage Rental Schedule",
      "11",
      <CalendarDays strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/rental-schedule-dashboard")
    ),

    canManageRevenue &&
    getItem(
      "Manage Revenue",
      "12",
      <ReceiptIndianRupee strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/manage-revenue")
    ),
    canManageRentalInvoice &&
    getItem(
      "Manage Rental Invoice",
      "13",
      <ReceiptText strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/manage-rental-invoice")
    ),
    canManagePostDisbursement &&
    getItem(
      "Manage Post Disbursement",
      "14",
      <StickyNote strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/manage-post-disbursement")
    ),
    canManagePortfolio &&
    getItem(
      "Manage Portfolio",
      "15",
      <Briefcase strokeWidth={1.5} style={{ fontSize: "20px" }} />,
      () => navigate("/manage-portfolio")
    ),
  ];

  const handleCompanySelect = (company: { id: string; name: string; cin: string }) => {
    useCustomerStore.getState().setCustomer(company);
    navigate(`/manage-customer/${company.id}`);
  };

  return (
    <Layout className="layout">
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeOut {
          from {
            opacity: 1;
            transform: translateX(0);
          }
          to {
            opacity: 0;
            transform: translateX(20px);
          }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      <Sider
        breakpoint="md"
        collapsedWidth="0"
        width={sidebarWidth}
        onMouseEnter={() => setIsSidebarHovered(true)}
        onMouseLeave={() => setIsSidebarHovered(false)}
        style={{
          background: "#FFFFFF",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingTop: "20px",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          flexDirection: "column",
          transition: "width 0.3s ease, opacity 0.3s ease",
          zIndex: 1000,
        }}
      >
        <div style={{ 
          margin: "0 16px 16px 16px",
          position: "relative",
          width: "auto",
          minHeight: "60px",
          height: "60px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}>
          <img 
            key="collapsed-logo"
            style={{ 
              position: "absolute",
              animation: isSidebarHovered ? "fadeOut 0.3s ease-in-out forwards" : "fadeIn 0.3s ease-in-out",
              opacity: isSidebarHovered ? 0 : 1,
              pointerEvents: isSidebarHovered ? "none" : "auto",
              maxWidth: "100%",
              maxHeight: "60px",
              height: "auto",
              width: "auto",
              objectFit: "contain"
            }} 
            src={logo_two} 
            alt="Logo"
          />
          <img 
            key="expanded-logo"
            style={{ 
              position: "absolute",
              animation: !isSidebarHovered ? "fadeOut 0.3s ease-in-out forwards" : "fadeIn 0.3s ease-in-out",
              opacity: isSidebarHovered ? 1 : 0,
              pointerEvents: !isSidebarHovered ? "none" : "auto",
              maxWidth: "100%",
              maxHeight: "60px",
              height: "auto",
              width: "auto",
              objectFit: "contain"
            }} 
            src={logo} 
            alt="Logo"
          />
        </div>
        <Menu 
          selectedKeys={[getSelectedKey()]} 
          mode="inline" 
          items={items.filter(Boolean) as MenuProps['items']}
          style={{ 
            width: "98%",
            transition: "opacity 0.3s ease",
            animation: isSidebarHovered ? "slideIn 0.3s ease-in-out" : "none"
          }}
        />
      </Sider>

      <Layout
        className="layout"
        style={{ marginLeft: sidebarWidth, background: "#f3f4f6", transition: "margin-left 0.3s ease" }}
      >
        <Header
          className="header z-50"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 20px",
            background: "rgb(255, 255, 255)",
            ...(firstSegment === "manage-customer" && secondSegment
              ? { position: "fixed", left: `${sidebarWidth}px`, width: `calc(100% - ${sidebarWidth}px)`, transition: "left 0.3s ease, width 0.3s ease" }
              : {}),
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "1%" }}>
            <img
              style={{ width: "16px", height: "16px" }}
              width={16}
              height={16}
              src={BreadcrumbIcon}
              alt="BreadcrumbIcon"
            />
            <Breadcrumb
              items={[
                ...breadcrumbItems,
                ...(firstSegment === "manage-customer" && secondSegment
                  ? [
                    {
                      title: (
                        <CompanySearchDropdown
                          currentCompany={companyName || ""}
                          onCompanySelect={handleCompanySelect}
                          companies={companies}
                        />
                      ),
                    },
                  ]
                  : []),
              ]}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "20px", marginRight: "5%" }}>
            <Badge count={2}>
              <BellOutlined style={{ fontSize: "20px" }} />
            </Badge>
            <Dropdown menu={profileMenu} trigger={["click"]}>
              <Avatar icon={<UserOutlined />} />
            </Dropdown>
            {details.name}
          </div>
        </Header>

        <div
          style={{
            width: "100%",
            minWidth: 0,
            padding: 0,
            margin: 0,
            background: "#ffffff",
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            overflowX: "hidden",
          }}
        >
          <Outlet />
        </div>
      </Layout>
    </Layout>
  );
};

export default DashboardLayout;
